import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const AdminActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newActivity, setNewActivity] = useState({
    name: '',
    category: 'Fitness Classes',
    description: '',
    day: 'Monday',
    time: '',
    duration: '',
    price: 0,
    capacity: 20,
    image: '/images/activities/default.jpg'
  });
  const { token } = useAuth();

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get('/api/activities/admin/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setLoading(false);
    }
  };

  const handleCreateActivity = async () => {
    if (!newActivity.name || !newActivity.description || !newActivity.time || !newActivity.duration) {
      alert('Please fill in all required fields!');
      return;
    }

    try {
      await axios.post('/api/activities/admin/create', newActivity, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Activity created successfully!');
      setShowCreateForm(false);
      setNewActivity({
        name: '',
        category: 'Fitness Classes',
        description: '',
        day: 'Monday',
        time: '',
        duration: '',
        price: 0,
        capacity: 20,
        image: '/images/activities/default.jpg'
      });
      fetchActivities();
    } catch (error) {
      alert('Failed to create activity: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (activity) => {
    setEditingId(activity._id);
    setEditForm({
      name: activity.name,
      category: activity.category,
      description: activity.description,
      day: activity.day,
      time: activity.time,
      duration: activity.duration,
      price: activity.price,
      capacity: activity.capacity,
      availableSpots: activity.availableSpots,
      image: activity.image
    });
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`/api/activities/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Activity updated successfully!');
      setEditingId(null);
      fetchActivities();
    } catch (error) {
      console.error('Error updating activity:', error);
      alert('Failed to update activity');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await axios.patch(`/api/activities/${id}/toggle`, 
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchActivities();
    } catch (error) {
      console.error('Error toggling activity status:', error);
    }
  };

  const handleToggleBooking = async (id, currentStatus) => {
    try {
      await axios.patch(`/api/activities/${id}/toggle-booking`, 
        { bookingOpen: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchActivities();
    } catch (error) {
      console.error('Error toggling booking status:', error);
    }
  };

  const handleUpdateSpots = async (id, change) => {
    try {
      const activity = activities.find(a => a._id === id);
      const newSpots = activity.availableSpots + change;

      if (newSpots < 0) {
        alert('Cannot have negative spots!');
        return;
      }

      if (newSpots > activity.capacity) {
        alert(`Cannot exceed capacity of ${activity.capacity}`);
        return;
      }

      await axios.put(`/api/activities/${id}`, 
        { availableSpots: newSpots },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      fetchActivities();
    } catch (error) {
      console.error('Error updating spots:', error);
      alert('Failed to update spots');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.delete(`/api/activities/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Activity deleted successfully!');
        fetchActivities();
      } catch (error) {
        console.error('Error deleting activity:', error);
        alert('Failed to delete activity');
      }
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Manage Activities</h1>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)} 
          style={styles.createBtn}
        >
          {showCreateForm ? '✕ Cancel' : '➕ Create New Activity'}
        </button>
      </div>
      
      <p style={styles.subtitle}>Edit capacity, pricing, and control when bookings are open</p>

      {showCreateForm && (
        <div style={styles.createForm}>
          <h2 style={styles.formTitle}>Create New Activity</h2>
          
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Activity Name *</label>
              <input 
                type="text"
                value={newActivity.name}
                onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
                style={styles.inputForm}
                placeholder="e.g., Morning Yoga"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Category *</label>
              <select 
                value={newActivity.category}
                onChange={(e) => setNewActivity({...newActivity, category: e.target.value})}
                style={styles.inputForm}
              >
                <option value="Fitness Classes">Fitness Classes</option>
                <option value="Sports">Sports</option>
                <option value="Monthly Events">Monthly Events</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Day *</label>
              <select 
                value={newActivity.day}
                onChange={(e) => setNewActivity({...newActivity, day: e.target.value})}
                style={styles.inputForm}
              >
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
                <option value="Sunday">Sunday</option>
                <option value="TBA">TBA (Monthly Events)</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Time *</label>
              <input 
                type="text"
                value={newActivity.time}
                onChange={(e) => setNewActivity({...newActivity, time: e.target.value})}
                style={styles.inputForm}
                placeholder="e.g., 9:30 AM - 11:00 AM"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Duration *</label>
              <input 
                type="text"
                value={newActivity.duration}
                onChange={(e) => setNewActivity({...newActivity, duration: e.target.value})}
                style={styles.inputForm}
                placeholder="e.g., 1.5 hours"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Price (KES) *</label>
              <input 
                type="number"
                value={newActivity.price}
                onChange={(e) => setNewActivity({...newActivity, price: Number(e.target.value)})}
                style={styles.inputForm}
                placeholder="0"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Capacity *</label>
              <input 
                type="number"
                value={newActivity.capacity}
                onChange={(e) => setNewActivity({...newActivity, capacity: Number(e.target.value)})}
                style={styles.inputForm}
                placeholder="20"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Image Path</label>
              <input 
                type="text"
                value={newActivity.image}
                onChange={(e) => setNewActivity({...newActivity, image: e.target.value})}
                style={styles.inputForm}
                placeholder="/images/activities/your-image.jpg"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description *</label>
            <textarea 
              value={newActivity.description}
              onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
              style={{...styles.inputForm, minHeight: '100px'}}
              placeholder="Describe the activity..."
            />
          </div>

          <button onClick={handleCreateActivity} style={styles.submitBtn}>
            Create Activity
          </button>
        </div>
      )}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Image</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Day</th>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Price</th>
              <th style={styles.th}>Capacity</th>
              <th style={styles.th}>Available</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Booking</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {activities.map(activity => (
              <tr key={activity._id} style={styles.tableRow}>
                <td style={styles.td}>
                  <img 
                    src={activity.image} 
                    alt={activity.name}
                    style={styles.thumbnail}
                    onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50"%3E%3Crect fill="%23ddd" width="50" height="50"/%3E%3C/svg%3E'}
                  />
                </td>
                <td style={styles.td}>
                  {editingId === activity._id ? (
                    <input 
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      style={styles.input}
                    />
                  ) : (
                    <strong>{activity.name}</strong>
                  )}
                </td>
                <td style={styles.td}>{activity.category}</td>
                <td style={styles.td}>
                  {editingId === activity._id ? (
                    <input 
                      type="text"
                      value={editForm.day}
                      onChange={(e) => setEditForm({...editForm, day: e.target.value})}
                      style={styles.input}
                    />
                  ) : (
                    activity.day
                  )}
                </td>
                <td style={styles.td}>
                  {editingId === activity._id ? (
                    <input 
                      type="text"
                      value={editForm.time}
                      onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                      style={styles.input}
                    />
                  ) : (
                    activity.time
                  )}
                </td>
                <td style={styles.td}>
                  {editingId === activity._id ? (
                    <input 
                      type="number"
                      value={editForm.price}
                      onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                      style={styles.inputSmall}
                    />
                  ) : (
                    `KES ${activity.price}`
                  )}
                </td>
                <td style={styles.td}>
                  {editingId === activity._id ? (
                    <input 
                      type="number"
                      value={editForm.capacity}
                      onChange={(e) => setEditForm({...editForm, capacity: e.target.value})}
                      style={styles.inputSmall}
                    />
                  ) : (
                    <span style={styles.capacity}>{activity.capacity}</span>
                  )}
                </td>
                <td style={styles.td}>
                  {editingId === activity._id ? (
                    <input 
                      type="number"
                      value={editForm.availableSpots}
                      onChange={(e) => setEditForm({...editForm, availableSpots: parseInt(e.target.value)})}
                      style={styles.inputSmall}
                    />
                  ) : (
                    <div style={styles.spotsControl}>
                      <button 
                        onClick={() => handleUpdateSpots(activity._id, -1)}
                        style={styles.spotBtn}
                        title="Decrease spot"
                      >
                        -
                      </button>
                      <span style={styles.spotsValue}>{activity.availableSpots}</span>
                      <button 
                        onClick={() => handleUpdateSpots(activity._id, 1)}
                        style={styles.spotBtn}
                        title="Increase spot"
                      >
                        +
                      </button>
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={activity.isActive ? styles.statusActive : styles.statusInactive}>
                    {activity.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={activity.bookingOpen ? styles.bookingOpen : styles.bookingClosed}>
                    {activity.bookingOpen ? 'Open' : 'Closed'}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={styles.actions}>
                    {editingId === activity._id ? (
                      <>
                        <button 
                          onClick={() => handleUpdate(activity._id)}
                          style={styles.btn}
                        >
                          Save
                        </button>
                        <button 
                          onClick={() => setEditingId(null)}
                          style={styles.btn}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleEdit(activity)}
                          style={styles.btn}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleToggleActive(activity._id, activity.isActive)}
                          style={styles.btn}
                        >
                          {activity.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button 
                          onClick={() => handleToggleBooking(activity._id, activity.bookingOpen)}
                          style={styles.btn}
                        >
                          {activity.bookingOpen ? 'Close Bookings' : 'Open Bookings'}
                        </button>
                        <button 
                          onClick={() => handleDelete(activity._id, activity.name)}
                          style={styles.btnDelete}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '1900px', margin: '0 auto', padding: '2rem', background: '#fafafa', minHeight: '100vh' },
  loading: { textAlign: 'center', padding: '4rem', fontSize: '1.2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  title: { fontSize: '2.5rem', color: '#1a5632', fontFamily: 'Playfair Display, serif', margin: 0 },
  subtitle: { color: '#5A5A5A', marginBottom: '2rem', fontSize: '1.1rem' },
  createBtn: { background: '#1a5632', color: 'white', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' },
  createForm: { background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 2px 15px rgba(0,0,0,0.08)', marginBottom: '2rem' },
  formTitle: { fontSize: '1.5rem', color: '#1a5632', marginBottom: '1.5rem' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { fontSize: '0.9rem', fontWeight: 600, color: '#2C2C2C' },
  inputForm: { padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' },
  submitBtn: { background: '#1a5632', color: 'white', padding: '1rem 2rem', border: 'none', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 600, cursor: 'pointer', width: '100%', maxWidth: '300px' },
  tableContainer: { overflowX: 'auto', background: 'white', borderRadius: '15px', boxShadow: '0 2px 15px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#fafafa', borderBottom: '2px solid #e0e0e0' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.9rem', color: '#2C2C2C' },
  tableRow: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '1rem', fontSize: '0.9rem', color: '#2C2C2C' },
  thumbnail: { width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' },
  input: { width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' },
  inputSmall: { width: '80px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' },
  capacity: { background: '#e8f5e9', color: '#1a5632', padding: '0.25rem 0.75rem', borderRadius: '12px', fontWeight: 600 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32', padding: '0.35rem 0.85rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 },
  statusInactive: { background: '#f5f5f5', color: '#757575', padding: '0.35rem 0.85rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 },
  bookingOpen: { background: '#e8f5e9', color: '#2e7d32', padding: '0.35rem 0.85rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 },
  bookingClosed: { background: '#ffebee', color: '#c62828', padding: '0.35rem 0.85rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 },
  spotsControl: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  spotsValue: { minWidth: '30px', textAlign: 'center', fontWeight: 700, color: '#1a5632' },
  spotBtn: { background: 'white', color: '#1a5632', width: '28px', height: '28px', border: '1px solid #7AB845', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700 },
  actions: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  btn: { background: 'white', color: '#2C2C2C', padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  btnDelete: { background: 'white', color: '#d32f2f', padding: '0.5rem 1rem', border: '1px solid #ffcdd2', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }
};

export default AdminActivities;